import { For, Show, createEffect, createSignal, onCleanup } from "solid-js";
import { cx } from "../../styled-system/css";
import { hexToHslColor, hslToHex, hslToLab } from "../color";
import { imageUrl, originalImageUrl } from "../data";
import { styles } from "../styles";
import type { CoordItem, HslColorFilter, Swatch } from "../types";
import { QrCarousel, type QrDisplayItem } from "./QrList";

export type ImagePreviewTarget = {
  kind: "item" | "coordinate";
  title: string;
  url: string;
  qrItems: QrDisplayItem[];
  palette?: Swatch[];
  metadata?: Array<{
    label: string;
    value: string;
    wide?: boolean;
  }>;
  showImage?: boolean;
  showColors?: boolean;
  eyedropperEnabled?: boolean;
  coordinateItems?: CoordItem[];
  relatedFriends?: Array<{
    key: string;
    name: string;
  }>;
  footerAction?: {
    label: string;
    onClick: () => void;
  };
};

type Props = {
  target: ImagePreviewTarget | null;
  selectedSearchColors: HslColorFilter[];
  qrCycleIntervalMs: number;
  onClose: () => void;
  onAddSearchColor: (swatch: Swatch) => void;
  onPreviewItem: (item: CoordItem) => void;
  onPreviewFriend: (key: string) => void;
};

function swatchFromHex(hex: string): Swatch {
  const color = hexToHslColor(hex);
  return {
    hex: hslToHex(color),
    hsl: [color.hue, color.saturation, color.lightness],
    lab: hslToLab(color),
    weight: 1,
  };
}

function PaletteChip(props: { swatch: Swatch; selected: boolean; onAdd: () => void }) {
  let longPressTimer: number | undefined;
  let suppressClick = false;
  const cancelLongPressTimer = () => {
    if (longPressTimer !== undefined) window.clearTimeout(longPressTimer);
    longPressTimer = undefined;
  };
  onCleanup(cancelLongPressTimer);

  return (
    <button
      class={styles.previewColorChip}
      classList={{ [styles.previewColorChipSelected]: props.selected }}
      type="button"
      title={`${props.swatch.hex}を色検索に追加`}
      aria-label={`${props.swatch.hex}を色検索に追加`}
      style={{ "background-color": props.swatch.hex }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        suppressClick = false;
        cancelLongPressTimer();
        longPressTimer = window.setTimeout(() => {
          suppressClick = true;
        }, 550);
      }}
      onPointerUp={cancelLongPressTimer}
      onPointerCancel={cancelLongPressTimer}
      onPointerLeave={cancelLongPressTimer}
      onContextMenu={(event) => event.preventDefault()}
      onClick={(event) => {
        if (suppressClick) {
          event.preventDefault();
          suppressClick = false;
          return;
        }
        props.onAdd();
      }}
    >
      <Show when={props.selected}><span class={styles.previewColorSelectedMark}>✓</span></Show>
    </button>
  );
}

function PreviewMetadata(props: {
  items: NonNullable<ImagePreviewTarget["metadata"]>;
  palette: Swatch[];
  selectedSearchColors: HslColorFilter[];
  eyedropperActive: boolean;
  eyedropperEnabled: boolean;
  showColors: boolean;
  eyedropperMessage: string;
  onStartEyedropper: () => void;
  onAddSearchColor: (swatch: Swatch) => void;
}) {
  const isSelected = (swatch: Swatch) => props.selectedSearchColors.some(
    (color) => (
      color.hue === swatch.hsl[0]
      && color.saturation === swatch.hsl[1]
      && color.lightness === swatch.hsl[2]
    ) || hslToHex(color).toLowerCase() === swatch.hex.toLowerCase(),
  );
  return (
    <div class={styles.previewMetadataRegion}>
      <dl class={styles.previewCoordinateMetadata}>
        <For each={props.items}>
          {(metadata) => (
            <div classList={{ [styles.previewCoordinateMetadataWide]: metadata.wide }}>
              <dt>{metadata.label}</dt>
              <dd>{metadata.value}</dd>
            </div>
          )}
        </For>
      </dl>
      <Show when={props.showColors}>
        <div class={styles.previewColors}>
          <strong>代表色</strong>
          <div class={styles.previewColorChips}>
            <For each={props.palette}>{(swatch) => (
              <PaletteChip
                swatch={swatch}
                selected={isSelected(swatch)}
                onAdd={() => props.onAddSearchColor(swatch)}
              />
            )}</For>
            <Show when={props.eyedropperEnabled}>
              <button
                class={styles.previewEyedropperButton}
                type="button"
                title="スポイトで画像から色を追加"
                aria-label="スポイトで画像から色を検索色に追加"
                aria-pressed={props.eyedropperActive}
                onClick={props.onStartEyedropper}
              >⌖</button>
            </Show>
          </div>
          <Show when={props.eyedropperEnabled}>
            <span class={styles.previewEyedropperHelp}>⌖ スポイトを選び、画像上で押したまま動かして離すと色を追加できます。</span>
          </Show>
          <Show when={props.eyedropperEnabled && props.eyedropperMessage}>
            <span class={styles.previewEyedropperMessage} role="status">{props.eyedropperMessage}</span>
          </Show>
        </div>
      </Show>
    </div>
  );
}

export function ImagePreviewModal(props: Props) {
  const [eyedropperActive, setEyedropperActive] = createSignal(false);
  const [eyedropperMessage, setEyedropperMessage] = createSignal("");
  const [eyedropperPreview, setEyedropperPreview] = createSignal<{
    hex: string;
    x: number;
    y: number;
  } | null>(null);
  let activePointer: number | undefined;
  let dragImage: HTMLImageElement | undefined;
  let queuedPoint: { image: HTMLImageElement; x: number; y: number } | undefined;
  let animationFrame: number | undefined;
  let samplingCanvas: HTMLCanvasElement | undefined;
  let previewImage: HTMLImageElement | undefined;

  const resetDrag = () => {
    if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    animationFrame = undefined;
    queuedPoint = undefined;
    window.removeEventListener("pointermove", movePicking);
    window.removeEventListener("pointerup", finishPicking);
    window.removeEventListener("pointercancel", cancelPicking);
    activePointer = undefined;
    dragImage = undefined;
    setEyedropperPreview(null);
  };

  const sampleImageColor = (image: HTMLImageElement, clientX: number, clientY: number) => {
    if (!image.complete || image.naturalWidth === 0 || image.naturalHeight === 0) {
      setEyedropperMessage("画像の読み込み完了後にもう一度お試しください。");
      return undefined;
    }
    const rect = image.getBoundingClientRect();
    const sourceX = Math.max(0, Math.min(
      image.naturalWidth - 1,
      Math.floor((clientX - rect.left) / rect.width * image.naturalWidth),
    ));
    const sourceY = Math.max(0, Math.min(
      image.naturalHeight - 1,
      Math.floor((clientY - rect.top) / rect.height * image.naturalHeight),
    ));
    samplingCanvas ??= document.createElement("canvas");
    samplingCanvas.width = 1;
    samplingCanvas.height = 1;
    const context = samplingCanvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      setEyedropperMessage("スポイトを初期化できませんでした。");
      return undefined;
    }
    try {
      context.drawImage(image, sourceX, sourceY, 1, 1, 0, 0, 1, 1);
      const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
      const hex = `#${[red, green, blue]
        .map((channel) => channel.toString(16).padStart(2, "0"))
        .join("")}`;
      setEyedropperPreview({ hex, x: clientX, y: clientY });
      return hex;
    } catch {
      resetDrag();
      setEyedropperActive(false);
      setEyedropperMessage("画像配信元の制限により、この画像から色を取得できませんでした。");
      return undefined;
    }
  };

  const flushQueuedPoint = () => {
    animationFrame = undefined;
    const point = queuedPoint;
    queuedPoint = undefined;
    if (point) sampleImageColor(point.image, point.x, point.y);
  };

  const startPicking = (event: PointerEvent & { currentTarget: HTMLImageElement }) => {
    if (
      !eyedropperActive()
      || (event.pointerType === "mouse" && event.button !== 0)
      || activePointer !== undefined
    ) return;
    event.preventDefault();
    resetDrag();
    activePointer = event.pointerId;
    dragImage = event.currentTarget;
    dragImage.setPointerCapture?.(event.pointerId);
    window.addEventListener("pointermove", movePicking, { passive: false });
    window.addEventListener("pointerup", finishPicking);
    window.addEventListener("pointercancel", cancelPicking);
    setEyedropperMessage("ドラッグして色を確認し、指またはボタンを離すと追加します。");
    if (!sampleImageColor(dragImage, event.clientX, event.clientY)) resetDrag();
  };

  const movePicking = (event: PointerEvent) => {
    if (event.pointerId !== activePointer || !dragImage) return;
    event.preventDefault();
    const samples = event.getCoalescedEvents?.() ?? [];
    const latest = samples.at(-1) ?? event;
    queuedPoint = { image: dragImage, x: latest.clientX, y: latest.clientY };
    if (animationFrame === undefined) animationFrame = requestAnimationFrame(flushQueuedPoint);
  };

  const finishPicking = (event: PointerEvent) => {
    if (event.pointerId !== activePointer || !dragImage) return;
    event.preventDefault();
    if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    animationFrame = undefined;
    queuedPoint = undefined;
    const hex = sampleImageColor(dragImage, event.clientX, event.clientY);
    if (hex) {
      props.onAddSearchColor(swatchFromHex(hex));
      setEyedropperActive(false);
      setEyedropperMessage(`${hex.toUpperCase()}を検索色に追加しました。`);
    }
    resetDrag();
  };

  const cancelPicking = (event: PointerEvent) => {
    if (event.pointerId !== activePointer) return;
    resetDrag();
    setEyedropperMessage("スポイト操作をキャンセルしました。");
  };

  const cancelEyedropper = (message = "") => {
    resetDrag();
    setEyedropperActive(false);
    setEyedropperMessage(message);
  };
  const toggleEyedropper = () => {
    if (eyedropperActive()) {
      cancelEyedropper("スポイト操作をキャンセルしました。");
      return;
    }
    resetDrag();
    setEyedropperActive(true);
    setEyedropperMessage("画像内で押してドラッグしてください。再度⌖を押すかEscapeでキャンセルできます。");
  };
  const addSearchColor = (swatch: Swatch) => {
    cancelEyedropper();
    props.onAddSearchColor(swatch);
  };
  const closeModal = () => {
    cancelEyedropper();
    props.onClose();
  };
  const previewItem = (item: CoordItem) => {
    cancelEyedropper();
    props.onPreviewItem(item);
  };

  createEffect(() => {
    if (!props.target) cancelEyedropper();
  });

  createEffect(() => {
    const active = eyedropperActive();
    props.target;
    const image = previewImage;
    if (!active || !image) return;
    const preventImageScroll = (event: TouchEvent) => event.preventDefault();
    image.addEventListener("touchmove", preventImageScroll, { passive: false });
    onCleanup(() => image.removeEventListener("touchmove", preventImageScroll));
  });

  onCleanup(resetDrag);

  return (
    <Show when={props.target}>
      {(target) => (
        <div class={target().qrItems.length ? styles.qrPreviewLayer : styles.previewLayer} role="presentation">
          <button class={styles.previewBackdrop} type="button" aria-label="画像表示を閉じる" onClick={closeModal} />
          <section
            class={styles.previewModal}
            classList={{
              [styles.previewModalItem]: target().kind === "item",
              [styles.previewModalItemWithQr]: target().kind === "item" && target().qrItems.length > 0,
              [styles.previewModalCoordinate]: target().kind === "coordinate",
            }}
            role="dialog"
            aria-modal="true"
            aria-label={`${target().title} 拡大表示`}
          >
            <header class={styles.previewHeader}>
              <h2>{target().title}</h2>
              <button class={styles.iconButton} type="button" aria-label="閉じる" onClick={closeModal}>✕</button>
            </header>
            <Show when={target().qrItems.length}>
              <QrCarousel items={target().qrItems} cycleIntervalMs={props.qrCycleIntervalMs} />
            </Show>
            <Show
              when={target().kind === "coordinate"}
              fallback={(
                <div class={styles.previewItemContent}>
                  <div class={`${styles.previewImageFrame} ${styles.previewImageFrameItem}`}>
                    <img
                      ref={previewImage}
                      classList={{ [styles.previewImageEyedropperActive]: eyedropperActive() }}
                      crossOrigin={target().eyedropperEnabled === false ? undefined : "anonymous"}
                      src={originalImageUrl(target().url)}
                      alt={target().title}
                      decoding="async"
                      draggable={false}
                      style={{
                        "pointer-events": eyedropperActive() ? "auto" : "none",
                        "touch-action": "manipulation",
                      }}
                      onDragStart={(event) => event.preventDefault()}
                      onPointerDown={startPicking}
                    />
                  </div>
                  <PreviewMetadata
                    items={target().metadata ?? []}
                    palette={target().palette ?? []}
                    selectedSearchColors={props.selectedSearchColors}
                    eyedropperActive={eyedropperActive()}
                    eyedropperEnabled={target().eyedropperEnabled !== false}
                    showColors={target().showColors !== false}
                    eyedropperMessage={eyedropperMessage()}
                    onStartEyedropper={toggleEyedropper}
                    onAddSearchColor={addSearchColor}
                  />
                </div>
              )}
            >
              <div class={styles.previewCoordinateContent}>
                <Show when={target().showImage !== false}>
                  <div class={styles.previewCoordinateImageFrame}>
                    <img
                      ref={previewImage}
                      classList={{ [styles.previewImageEyedropperActive]: eyedropperActive() }}
                      crossOrigin={target().eyedropperEnabled === false ? undefined : "anonymous"}
                      src={originalImageUrl(target().url)}
                      alt={target().title}
                      decoding="async"
                      draggable={false}
                      style={{
                        "pointer-events": eyedropperActive() ? "auto" : "none",
                        "touch-action": "manipulation",
                      }}
                      onDragStart={(event) => event.preventDefault()}
                      onPointerDown={startPicking}
                    />
                  </div>
                </Show>
                <PreviewMetadata
                  items={target().metadata ?? []}
                  palette={target().palette ?? []}
                  selectedSearchColors={props.selectedSearchColors}
                  eyedropperActive={eyedropperActive()}
                  eyedropperEnabled={target().eyedropperEnabled !== false}
                  showColors={target().showColors !== false}
                  eyedropperMessage={eyedropperMessage()}
                  onStartEyedropper={toggleEyedropper}
                  onAddSearchColor={addSearchColor}
                />
                <Show when={target().coordinateItems?.length}>
                  <div class={styles.previewConstituentItems} aria-label="構成アイテム">
                    <For each={target().coordinateItems}>
                      {(item) => (
                        <button
                          class={cx(styles.childButton, styles.previewConstituentItemButton)}
                          type="button"
                          title={`${item.term}を拡大表示`}
                          aria-label={`${item.name} ${item.term}を拡大表示`}
                          onClick={() => previewItem(item)}
                        >
                          <img
                            class={cx(styles.image, styles.thumbnailImage)}
                            src={imageUrl(item.url)}
                            alt={`${item.name} ${item.term}`}
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                          />
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={target().footerAction}>
                  {(action) => (
                    <div class={styles.previewFooterActions}>
                      <button
                        class={styles.secondaryButton}
                        type="button"
                        onClick={() => {
                          const handler = action().onClick;
                          closeModal();
                          handler();
                        }}
                      >{action().label}</button>
                    </div>
                  )}
                </Show>
                <Show when={target().relatedFriends?.length}>
                  <section class={styles.previewRelatedFriends} aria-label="フレカ">
                    <strong>フレカ</strong>
                    <div>
                      <For each={target().relatedFriends}>
                        {(friend) => (
                          <button
                            class={styles.secondaryButton}
                            type="button"
                            onClick={() => props.onPreviewFriend(friend.key)}
                          >{friend.name}</button>
                        )}
                      </For>
                    </div>
                  </section>
                </Show>
              </div>
            </Show>
          </section>
          <Show when={eyedropperPreview()}>
            {(preview) => (
              <div
                class={styles.previewEyedropperCursor}
                style={{
                  left: `${preview().x}px`,
                  top: `${preview().y}px`,
                  transform: preview().y < 80
                    ? "translate(-50%, 16px)"
                    : "translate(-50%, calc(-100% - 16px))",
                }}
                aria-hidden="true"
              >
                <span style={{ "background-color": preview().hex }} />
                <code>{preview().hex.toUpperCase()}</code>
              </div>
            )}
          </Show>
        </div>
      )}
    </Show>
  );
}
